/* eslint-disable @typescript-eslint/no-explicit-any */

import { FocusScope } from '@react-aria/focus';
import { flexRender, getCoreRowModel, useReactTable, type Cell, type ColumnDef } from '@tanstack/react-table';
import { assert } from 'assert-ts';
import cn from 'classnames';
import { useMemo } from 'react';
import styles from './Table.module.css';

type Props<Data extends Record<string, any>> = {
  /** The column schema */
  columns: Array<ColumnDef<Data, any>>;
  /** Data to populate into the table rows */
  data: Data[];
  /** A description of the table, for screen readers.  Not shown visually */
  caption?: string;
  /** Extra classNames to add to the table element */
  className?: string;
  /** Fit 100% of the container width and scroll the overflow. */
  hideOverflowX?: boolean;
  /**
   * Make the declared column sizes authoritative (engages `table-layout: fixed`): columns render at
   * their declared size and cell content truncates, instead of wide content stretching its column.
   * With this set, the table's minimum width is exactly the sum of the visible columns' sizes. A
   * column marked `meta: { fill: true }` is the exception: it absorbs whatever width is left after
   * the other columns take their declared sizes, and its declared size acts as its minimum.
   */
  fixedLayout?: boolean;
  /** Does this table contain short or double-height rows? */
  rowHeight?: 'short' | 'normal' | 'tall';
  /**
   * Optional list of column ids to hide (e.g. due to feature flags).
   * Note this cannot be changed after the component is rendered.
   */
  hiddenColumnIds?: string[];
};

/**
 * Table is a mostly-generic table, using react-table and applying a few base styles.
 */
export function Table<Data extends Record<string, any>>({
  data,
  columns,
  caption,
  className,
  hideOverflowX,
  fixedLayout,
  rowHeight = 'normal',
  hiddenColumnIds,
}: Props<Data>) {
  // defaultColumn is only used when the attributes are not provided in the column schema
  const defaultColumn = useMemo(
    () => ({
      // Width values are required when using the useFlexLayout
      minSize: 30, // minWidth is only used as a limit for resizing
      size: 150, // width is used for both the flex-basis and flex-grow
      maxSize: 400, // maxWidth is only used as a limit for resizing
    }),
    []
  );
  const columnVisibility = hiddenColumnIds?.reduce<Record<string, boolean>>((accum, colId) => {
    accum[colId] = false;
    return accum;
  }, {});
  // eslint-disable-next-line react-hooks/incompatible-library -- React Compiler cannot memoize useReactTable
  const table = useReactTable<Data>({
    columns,
    data,
    defaultColumn,
    getCoreRowModel: getCoreRowModel(),
    state: { columnVisibility: columnVisibility },
  });

  /**
   * Creates a style object and class name according to properties on the cell.
   */
  function getCellStyles(cell: Cell<Data, unknown>) {
    const { column } = cell;
    const { truncate, align, className: metaClassName, fill } = column.columnDef.meta ?? {};

    // A fill column declares no width, so under `table-layout: fixed` the leftover width flows to
    // it; the table's min-width below keeps that leftover at or above the column's declared size.
    // Fill only means something in fixed layout, so elsewhere the declared width still applies.
    const style: React.CSSProperties = fill && fixedLayout ? {} : { width: column.getSize() };

    return [
      style,
      cn(metaClassName, {
        truncate,
        'text-center': align === 'center',
        'text-right': align === 'right',
      }),
    ] as const;
  }

  // A fixed-layout table's implicit minimum is the sum of its rendered column widths, but a fill
  // column contributes none of its own, so a table containing one declares the full sum (the fill
  // column's declared size included) to keep that column from shrinking below its size. Tables
  // without a fill column must NOT get this floor: the implicit minimum excludes columns hidden by
  // CSS media queries (e.g. HostsTable's column-hide breakpoints), while this sum - built from
  // TanStack's visibility model - would still count them and force horizontal scrolling at the
  // widths those breakpoints exist to serve.
  const visibleColumns = table.getVisibleLeafColumns();
  const fillColumns = visibleColumns.filter((column) => column.columnDef.meta?.fill);
  // Two fill columns would split the surplus while the min-width sum still counts both declared
  // sizes, so neither column's floor holds individually.
  assert(fillColumns.length <= 1, 'a fixedLayout Table supports at most one fill column');
  const minWidth =
    fixedLayout && fillColumns.length > 0 ?
      visibleColumns.reduce((sum, column) => sum + column.getSize(), 0)
    : undefined;

  return (
    <div
      className={cn(styles.Table_wrapper, className, {
        [styles.Table_wrapper___overflowXAuto]: hideOverflowX,
        [styles.Table_wrapper___fixedLayout]: fixedLayout,
      })}
    >
      {/** Keep focus on the next table row when a table row is deleted. */}
      <FocusScope>
        <table className={cn(styles.Table)} style={minWidth === undefined ? undefined : { minWidth }}>
          {caption && <caption className="visually-hidden">{caption}</caption>}
          <thead className={styles.Table_thead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const headerCellElement = flexRender(header.column.columnDef.header, header.getContext());
                  const { align, className: metaClassName, fill } = header.column.columnDef.meta ?? {};
                  const style: React.CSSProperties = fill && fixedLayout ? {} : { width: header.column.getSize() };

                  return (
                    <th
                      key={header.id}
                      className={cn(styles.Table_th, metaClassName, {
                        'text-center': align === 'center',
                        'text-right': align === 'right',
                      })}
                      style={style}
                    >
                      {headerCellElement}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody className={styles.Table_tbody}>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const cellElement = flexRender(cell.column.columnDef.cell, cell.getContext());
                    const [style, cellClassName] = getCellStyles(cell);
                    return (
                      <td
                        key={cell.id}
                        className={cn(styles.Table_td, cellClassName, styles[`Table_td___${rowHeight}`])}
                        style={style}
                      >
                        {cellElement}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </FocusScope>
    </div>
  );
}
